import type { TenantPushUser } from '../PushAPI';

/** The customer roles an alert's push action can address (PUSH_NOTI.md 9b: "Rollen heute"). */
export const PUSH_ROLE_IDS = ['customerAdministrator', 'billingAdministrator', 'technicalAdministrator'] as const;

export type PushRoleId = (typeof PUSH_ROLE_IDS)[number];

/** Locale key of a role's human label (shared with the team management). */
export const roleLabelKey = (roleId: string): string => `settingsPanel.userManagementPanel.roleName_${roleId}`;

/** "Vorname Nachname", falling back to the e-mail address when the user has no name. */
export const pushUserName = (user: Pick<TenantPushUser, 'firstName' | 'lastName' | 'email'>): string => {
    const name = [user.firstName, user.lastName]
        .map((part) => (part ?? '').trim())
        .filter(Boolean)
        .join(' ');

    return name === '' ? user.email : name;
};

/** A role assignment may carry a scope (`technicalAdministrator:<id>`); the base id decides. */
export const userHasRole = (user: Pick<TenantPushUser, 'roles'>, roleId: string): boolean =>
    (user.roles ?? []).some((role) => role === roleId || role.startsWith(`${roleId}:`));

/**
 * What the administration table and its filter mean by a user's push state:
 * `active` = at least one active device, `none` = no device at all, `off` = devices exist but none is active.
 */
export type PushState = 'active' | 'none' | 'off';

export const pushStateOf = (user: Pick<TenantPushUser, 'pushActive' | 'devices'>): PushState =>
    user.pushActive ? 'active' : (user.devices ?? []).length === 0 ? 'none' : 'off';

export type PushFilter = 'all' | PushState;

export const filterPushUsers = (users: TenantPushUser[], filter: PushFilter): TenantPushUser[] =>
    filter === 'all' ? users : users.filter((user) => pushStateOf(user) === filter);

/** Case-insensitive match on name and e-mail. */
export const matchesPushUser = (user: TenantPushUser, query: string): boolean => {
    const needle = query.trim().toLowerCase();

    return needle === '' || `${pushUserName(user)} ${user.email}`.toLowerCase().includes(needle);
};

export interface RoleReach {
    roleId: string;
    members: TenantPushUser[];
}

export interface PushReach {
    /** One entry per chosen role, with all its members (with and without push). */
    roles: RoleReach[];
    /** The individually chosen users that exist in the tenant. */
    people: TenantPushUser[];
    /** Chosen users the tenant's user list does not know (deleted since). */
    missingUsers: string[];
    /** Distinct users reachable by push across roles and individuals. */
    reachable: number;
    /** Distinct users addressed who have no active device. */
    unreachable: number;
}

/**
 * Who a push action addresses and who of them has push: the members of every chosen role plus the
 * chosen individuals, each person counted once.
 */
export const pushReachOf = (
    users: TenantPushUser[],
    recipientRoles: string[],
    recipientUsers: string[],
): PushReach => {
    const roles = recipientRoles.map((roleId) => ({
        roleId,
        members: users.filter((user) => userHasRole(user, roleId)),
    }));
    const people = recipientUsers
        .map((userId) => users.find((user) => user.userId === userId))
        .filter((user): user is TenantPushUser => user !== undefined);
    const missingUsers = recipientUsers.filter((userId) => !users.some((user) => user.userId === userId));

    const addressed = new Map<string, TenantPushUser>();

    roles.forEach((role) => role.members.forEach((member) => addressed.set(member.userId, member)));
    people.forEach((person) => addressed.set(person.userId, person));

    const all = Array.from(addressed.values());
    const reachable = all.filter((user) => user.pushActive).length;

    return { roles, people, missingUsers, reachable, unreachable: all.length - reachable };
};

/**
 * Whether the signed-in user has push themselves, from the tenant's user list: `unknown` while the list is not
 * there (first load, failed), `inactive` when it is and the user is not in it or has no active device.
 */
export const ownPushStateOf = (
    state: { status: string; users: TenantPushUser[] },
    authentication: { id?: string; user?: string; email?: string } | null | undefined,
): 'active' | 'inactive' | 'unknown' => {
    // A reload keeps the people it had, so the answer does not flicker to "unknown" while it runs.
    const reloading = state.status === 'loading' && state.users.length > 0;

    if (state.status !== 'ready' && !reloading) {
        return 'unknown';
    }

    const ids = [authentication?.id, authentication?.user].filter((id): id is string => typeof id === 'string' && id !== '');
    const email = (authentication?.email ?? '').trim().toLowerCase();
    const self = state.users.find(
        (user) => ids.includes(user.userId) || (email !== '' && user.email.trim().toLowerCase() === email),
    );

    return self?.pushActive ? 'active' : 'inactive';
};
